// Styles
import "./form.scss";
// Tools
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { AdGroupDraftValidationProvider } from "./context/ad-group-draft-validation.context";
import * as FormComponents from "./imports/component-imports";
import * as FormServices from "./imports/service-imports";
import * as FormUtils from "./imports/utils-import";
// Чтение и запись черновика формы в localStorage по sessionId
import { getFormState, setFormState } from "./utils/form-state-storage";
import {
	getRawGeoCountriesCatalog,
	getRawLanguagesCatalog,
	setRawGeoCountriesCatalog,
	setRawLanguagesCatalog,
} from "./utils/catalog-lists-storage";
import type {
	GeoCountry,
	Language,
} from "../../../../../../../../shared/api/catalogs/catalogs";
// Types
import { RootState, store } from "../../../../../../../../shared/store/store";
import type {
	ICampaignWizardForm,
	IConversionCatalogItem,
} from "../../../../../../../../shared/types/form.type";
// Actions
import {
	setActiveAdAccountId,
	markCampaignFormUpdateApplied,
} from "../../../../../../../../shared/store/slices/session.slice";
import { setSelectedCustomerAccountIdFromCustomerId } from "../dialog/utils/selectedCustomerIdStorage";

// Форма мастера кампании Google Ads: синхронизация с сессией, справочники (страны/языки/конверсии), отправка через WebSocket.
export const Form = () => {
	// --------------- Tools
	const dispatch = useDispatch<any>();
	const textLoaderRef = useRef<HTMLDivElement | null>(null);
	const freezeUiWasActiveRef = useRef(false);
	// Блокирует автосохранение во время гидратации формы
	const skipFormPersistRef = useRef(false);

	// --------------- Data
	const { data: dashboardData } = FormServices.useDashboardData();
	const { mutateAsync: createSession } = FormServices.useCreateSession();

	// --------------- State
	// Global
	const userId: number | null = useSelector(
		(state: RootState) => state.user?.buyer_id ?? null,
	);
	const activeAdAccountId = useSelector(
		(state: RootState) => state.session.activeAdAccountId,
	);
	const isFreezeUI: boolean = useSelector(
		(state: RootState) => state.session.freezeUI,
	);
	const sessionId: string | null = useSelector(
		(state: RootState) => state.session.sessionId,
	);
	// Черновик формы, пришедший по WS campaign_form.update (basic-режим)
	const campaignFormUpdate = useSelector(
		(state: RootState) => state.session.campaignFormUpdate,
	);
	// seq черновика, уже применённого к форме (каждый применяем ровно один раз)
	const appliedCampaignFormUpdateSeq = useSelector(
		(state: RootState) => state.session.appliedCampaignFormUpdateSeq,
	);

	// Countries
	const [countriesLoadingStatus, setCountriesLoadingStatus] =
		useState<boolean>(false);
	const [countriesItems, setCountriesItems] = useState<
		{ key: string; node: React.ReactNode }[]
	>([]);
	const [selectedCountries, setSelectedCountries] = useState<
		{ key: string; node: React.ReactNode }[]
	>([]);
	// Languages
	const [languagesLoadingStatus, setLanguagesLoadingStatus] =
		useState<boolean>(false);
	const [languagesItems, setLanguagesItems] = useState<
		{ key: string; node: React.ReactNode }[]
	>([]);
	const [selectedLanguages, setSelectedLanguages] = useState<
		{ key: string; node: React.ReactNode }[]
	>([]);

	// Dates
	const [startDateError, setStartDateError] = useState<string>("");
	const [endDateError, setEndDateError] = useState<string>("");

	// Conversions
	const [conversionsCatalog, setConversionsCatalog] = useState<
		IConversionCatalogItem[]
	>([]);
	const [conversionsCatalogLoading, setConversionsCatalogLoading] =
		useState(false);
	// true только после завершённой загрузки каталога конверсий для текущего app_id.
	// До этого pruning selective_optimization не запускаем — иначе затираем черновик
	// ещё не загруженным (пустым) каталогом.
	const [conversionsCatalogResolved, setConversionsCatalogResolved] =
		useState(false);

	// key змушує GroupsInput перемонтуватись і показати знімок сесії.
	const [fieldArrayMountKey, setFieldArrayMountKey] = useState(0);

	const [adGroupDraftBlockingKeys, setAdGroupDraftBlockingKeys] = useState(
		() => new Set<string>(),
	);

	const reportHeadlineDraftBlocking = useCallback(
		(fieldId: string, blocking: boolean) => {
			setAdGroupDraftBlockingKeys((prev) => {
				const key = `h:${fieldId}`;
				const next = new Set(prev);
				if (blocking) next.add(key);
				else next.delete(key);
				return next;
			});
		},
		[],
	);

	const reportDescriptionDraftBlocking = useCallback(
		(fieldId: string, blocking: boolean) => {
			setAdGroupDraftBlockingKeys((prev) => {
				const key = `d:${fieldId}`;
				const next = new Set(prev);
				if (blocking) next.add(key);
				else next.delete(key);
				return next;
			});
		},
		[],
	);

	useEffect(() => {
		setAdGroupDraftBlockingKeys(new Set());
	}, [fieldArrayMountKey]);

	const adGroupDraftValidationApi = useMemo(
		() => ({
			reportHeadlineDraftBlocking,
			reportDescriptionDraftBlocking,
		}),
		[reportHeadlineDraftBlocking, reportDescriptionDraftBlocking],
	);

	const adGroupDraftsBlockSubmit = adGroupDraftBlockingKeys.size > 0;

	// --------------- Form
	const formMethods = useFormContext<ICampaignWizardForm>();

	const {
		control,
		register,
		handleSubmit,
		reset,
		watch,
		setValue,
		getValues,
		formState: { isDirty }, // Для сохранения только после правок пользователя
	} = formMethods;

	const targetCpaWatch = watch("campaign_context.campaign.target_cpa_micros");
	const targetCpaErrorMessage = useMemo(
		() => FormUtils.getTargetCpaMicrosValidationMessage(targetCpaWatch),
		[targetCpaWatch],
	);

	const targetCpaRegister = register(
		"campaign_context.campaign.target_cpa_micros",
		{
			validate: (value) =>
				FormUtils.getTargetCpaMicrosValidationMessage(value) ?? true,
		},
	);

	// Валидация бюджета на отрицательное значение
	const budgetAmountWatch = watch("campaign_context.budget.amount_micros");
	const budgetAmountErrorMessage = useMemo(
		() => FormUtils.getBudgetAmountMicrosValidationMessage(budgetAmountWatch),
		[budgetAmountWatch],
	);
	const budgetAmountRegister = register(
		"campaign_context.budget.amount_micros",
		{
			validate: (value) =>
				FormUtils.getBudgetAmountMicrosValidationMessage(value) ?? true,
		},
	);

	const campaignNameRegister = register("campaign_context.campaign.name");
	const campaignNameWatch = watch("campaign_context.campaign.name");

	const campaignNameError = useMemo(() => {
		const issue = FormUtils.getCampaignNameValidationIssue(
			campaignNameWatch ?? "",
		);
		if (issue === "too_long")
			return "Назва кампанії не може перевищувати 255 символів";
		if (issue === "invalid_chars")
			return "Назва кампанії містить недопустимі символи";
		return undefined;
	}, [campaignNameWatch]);

	const appIdRegister = register("campaign_context.campaign.app_id");

	const ALL_KEY = "__all__";
	const SELECTIVE_OPTIMIZATION_FIELD =
		"campaign_context.campaign.selective_optimization_conversion_actions" as const;

	// Обновляет каталог конверсий по текущему app_id, аккаунту и сессии из стора (без лишних перезапусков эффектов).
	const refreshConversions = useCallback(async () => {
		const st = store.getState();
		const sid = st.session.sessionId;
		const buyerIdNum = Number(st.user?.buyer_id);
		const activeAccId = st.session.activeAdAccountId;
		const appTrimmed =
			getValues("campaign_context.campaign.app_id")?.trim() ?? "";
		if (Number.isNaN(buyerIdNum) || !activeAccId.trim() || !appTrimmed) {
			setConversionsCatalogLoading(false);
			setConversionsCatalog([]);
			setConversionsCatalogResolved(false);
			return;
		}
		setConversionsCatalogLoading(true);
		setConversionsCatalogResolved(false);
		setConversionsCatalog([]);
		try {
			const items = await FormServices.fetchConversionsCatalog({
				appIdTrimmed: appTrimmed,
				buyerId: buyerIdNum,
				accountIdFormattedForApi: activeAccId,
				isSessionStillActive: () => store.getState().session.sessionId === sid,
			});
			if (store.getState().session.sessionId === sid)
				setConversionsCatalog(items);
		} finally {
			if (store.getState().session.sessionId === sid) {
				setConversionsCatalogLoading(false);
				setConversionsCatalogResolved(true);
			}
		}
	}, [getValues]);

	const refreshConversionsRef = useRef(refreshConversions);
	refreshConversionsRef.current = refreshConversions;

	// Общая логика применения состояния формы к UI (из localStorage, API или WS-обновления)
	const applyFormToUi = useCallback(async (merged: ICampaignWizardForm) => {
		// Новые значения могут принадлежать другому app_id — каталог конверсий
		// считается неподтверждённым, пока его не перезагрузят эффекты ниже.
		setConversionsCatalogResolved(false);
		reset(merged, {
			keepDirtyValues: false,
			keepErrors: false,
			keepTouched: false,
		});
		setFieldArrayMountKey((k) => k + 1);

		const customerId = merged.campaign_context.customer_id;
		const formattedAccountId = customerId
			? FormUtils.formatCustomerIdToAccountId(customerId)
			: null;
		// Если меняем аккаунт — конверсии подгрузит эффект по activeAdAccountId
		const accountWillChange =
			formattedAccountId != null &&
			formattedAccountId !== store.getState().session.activeAdAccountId;

		if (formattedAccountId) {
			setSelectedCustomerAccountIdFromCustomerId(customerId);
			dispatch(setActiveAdAccountId(formattedAccountId));
		}

		if (merged.campaign_context.campaign.app_id?.trim()) {
			if (!accountWillChange) {
				await refreshConversionsRef.current();
			}
		} else {
			setConversionsCatalog([]);
			setConversionsCatalogLoading(false);
		}
	}, [reset, dispatch]);

	// При смене sessionId подгружает форму из localStorage или снимок с API; догружает конверсии при наличии app_id.
	useEffect(() => {
		let cancelled = false;

		async function hydrateFromSession() {
			skipFormPersistRef.current = true; // Не пишем в localStorage во время загрузки
			try {
				const buyer = userId;
				const emptyBase = FormUtils.createEmptyCampaignWizardForm(
					String(buyer != null ? buyer : ""),
				);
				if (!sessionId || buyer == null) {
					reset(emptyBase, {
						keepDirtyValues: false,
						keepErrors: false,
						keepTouched: false,
					});
					setConversionsCatalog([]);
					setConversionsCatalogLoading(false);
					setFieldArrayMountKey((k) => k + 1);
					return;
				}

				const expectedSessionId = sessionId;

				const isStillThisSession = () =>
					!cancelled &&
					store.getState().session.sessionId === expectedSessionId;

				const savedFormState = getFormState(expectedSessionId);

				// Если есть черновик — берём его вместо снимка с бэкенда
				if (savedFormState) {
					const merged: ICampaignWizardForm = {
						...savedFormState,
						user_id: String(buyer),
					};
					if (!isStillThisSession()) return;
					await applyFormToUi(merged);
					return;
				}

				const payload = await FormServices.fetchSessionFormPayload(
					expectedSessionId,
					String(buyer),
					isStillThisSession,
				);
				if (!isStillThisSession()) return;

				// Пока снимок шел с API, мог прийти WS-черновик campaign_form.update
				// и уже примениться — не затираем его пустым/устаревшим снимком.
				const appliedUpdate = store.getState().session.campaignFormUpdate;
				if (
					appliedUpdate != null &&
					appliedUpdate.sessionId === expectedSessionId
				)
					return;

				const merged =
					payload != null
						? FormUtils.mergeSessionPayloadIntoCampaignForm(emptyBase, payload)
						: emptyBase;
				await applyFormToUi(merged);
			} finally {
				skipFormPersistRef.current = false; // Снова разрешаем автосохранение
			}
		}

		void hydrateFromSession();
		return () => {
			cancelled = true;
		};
	}, [sessionId, userId, reset, dispatch, applyFormToUi]);

	// Применяет черновик из WS campaign_form.update к форме (basic-режим:
	// бэкенд присылает сгенерированный черновик после обработки промпта).
	// Мержим поверх текущих значений, сохраняем в localStorage и обновляем UI.
	useEffect(() => {
		if (!campaignFormUpdate || !sessionId) return;
		if (campaignFormUpdate.sessionId !== sessionId) return;
		// Каждый черновик применяем ровно один раз: при ремоунте формы или
		// возврате в сессию повторный merge затирал бы ручные правки пользователя
		// (гидратация восстановит форму из localStorage вместе с правками).
		if (campaignFormUpdate.seq === appliedCampaignFormUpdateSeq) return;

		const merged = FormUtils.mergeSessionPayloadIntoCampaignForm(
			getValues() as ICampaignWizardForm,
			campaignFormUpdate.payload,
		);
		merged.user_id = String(userId != null ? userId : "");

		// Сохраняем сразу: черновик должен пережить перезагрузку страницы
		setFormState(sessionId, merged);
		dispatch(markCampaignFormUpdateApplied(campaignFormUpdate.seq));

		skipFormPersistRef.current = true;
		void applyFormToUi(merged).finally(() => {
			skipFormPersistRef.current = false;
		});
	}, [
		campaignFormUpdate,
		appliedCampaignFormUpdateSeq,
		sessionId,
		userId,
		getValues,
		applyFormToUi,
		dispatch,
	]);

	const isInitialActiveAdAccountConversionsEffect = useRef(true);

	// При смене выбранного в шапке Google Ads аккаунта перезагружаем каталог конверсий для текущего app_id.
	// Первый запуск пропускаем: начальную загрузку выполняет hydrateFromSession (иначе гонка до reset формы).
	useEffect(() => {
		if (isInitialActiveAdAccountConversionsEffect.current) {
			isInitialActiveAdAccountConversionsEffect.current = false;
			return;
		}
		void refreshConversionsRef.current();
	}, [activeAdAccountId]);

	const formValues = watch();

	// Сохраняем изменения формы в localStorage для текущей сессии
	useEffect(() => {
		if (!sessionId || skipFormPersistRef.current || !isDirty) return;
		setFormState(sessionId, formValues as ICampaignWizardForm);
	}, [formValues, sessionId, isDirty]);

	// Пункты выпадающего списка конверсий из загруженного каталога.
	const conversionsSelectItems = useMemo(
		() =>
			conversionsCatalog.map((c) => ({
				key: c.key,
				node: <span>{c.name}</span>,
			})),
		[conversionsCatalog],
	);

	// Після завантаженого каталогу узгоджуємо ключі з поточним app_id: валідні
	// resource names лишаються, семантичні goal names чернетки basic-режиму
	// ("first_open", "in_app_purchase") підставляються на єдиний збіг за
	// category/type/name каталогу, решта (без однозначного збігу) відкидається.
	// Обидва порядки (чернетка до/після каталогу) збігаються тут: merge пише
	// ключі як є, а цей ефект запускається лише після resolved-каталогу.
	useEffect(() => {
		if (conversionsCatalogLoading || !conversionsCatalogResolved) return;
		const current = getValues(SELECTIVE_OPTIMIZATION_FIELD) ?? [];
		const { keys: resolvedKeys, substitutions } =
			FormUtils.resolveConversionKeysAgainstCatalog(
				current,
				conversionsCatalog,
			);
		if (
			resolvedKeys.length === current.length &&
			resolvedKeys.every((k, i) => k === current[i])
		)
			return;
		if (substitutions.length > 0 && process.env.NODE_ENV === "development") {
			console.debug(
				"[campaign-form] mapped draft conversion goals to catalog resource names:",
				substitutions,
			);
		}
		setValue(SELECTIVE_OPTIMIZATION_FIELD, resolvedKeys, {
			shouldDirty: true,
		});
	}, [
		conversionsCatalog,
		conversionsCatalogLoading,
		conversionsCatalogResolved,
		getValues,
		setValue,
	]);

	// Выбранные конверсии: ключи из формы сопоставляются с элементами каталога (иначе показываем key как текст).
	const selectedConversions = useMemo(() => {
		const keys =
			formValues?.campaign_context?.campaign
				?.selective_optimization_conversion_actions ?? [];
		return keys.map((key) => {
			const item = conversionsSelectItems.find((i) => i.key === key);
			return item ?? { key, node: <span>{key}</span> };
		});
	}, [formValues, conversionsSelectItems]);

	// Конверсии, ещё не добавленные в selective_optimization (доступны к выбору в селекте).
	const availableConversions = useMemo(() => {
		const filtered = conversionsSelectItems.filter(
			(i) => !selectedConversions.some((s) => s.key === i.key),
		);

		if (selectedConversions.length === 0) {
			const appIdTrimmed =
				formValues?.campaign_context?.campaign?.app_id?.trim() ?? "";
			const catalogEmptyAfterLoad =
				conversionsSelectItems.length === 0 && !conversionsCatalogLoading;

			let placeholderLabel: string;
			if (!appIdTrimmed && !conversionsCatalogLoading) {
				placeholderLabel =
					"— Спочатку введіть ідентифікатор програми (app_id) —";
			} else if (catalogEmptyAfterLoad && appIdTrimmed) {
				placeholderLabel = "— Конверсії не знайдені —";
			} else {
				placeholderLabel = "— Оберіть конверсію —";
			}

			return [
				{
					key: ALL_KEY,
					node: <span>{placeholderLabel}</span>,
				},
				...filtered,
			];
		}

		if (filtered.length === 0) {
			return [
				{
					key: ALL_KEY,
					node: <span>— Конверсій більше немає —</span>,
				},
			];
		}

		return filtered;
	}, [
		conversionsSelectItems,
		selectedConversions,
		conversionsCatalogLoading,
		formValues?.campaign_context?.campaign?.app_id,
	]);

	// Страны, доступные для добавления; при пустом выборе показываем псевдо-пункт «все страны».
	const availableCountries = useMemo(() => {
		const filtered = countriesItems.filter(
			(i) => !selectedCountries.some((s) => s.key === i.key),
		);
		if (selectedCountries.length === 0) {
			return [
				{ key: ALL_KEY, node: <span>— Оберіть країну —</span> },
				...filtered,
			];
		}
		return filtered;
	}, [countriesItems, selectedCountries]);

	// Языки, доступные для добавления; при пустом выборе — псевдо-пункт «все языки».
	const availableLanguages = useMemo(() => {
		const filtered = languagesItems.filter(
			(i) => !selectedLanguages.some((s) => s.key === i.key),
		);
		if (selectedLanguages.length === 0) {
			return [
				{ key: ALL_KEY, node: <span>— Оберіть мову —</span> },
				...filtered,
			];
		}
		return filtered;
	}, [languagesItems, selectedLanguages]);

	const geoKeys = watch("campaign_context.campaign.geo_targets");
	const langKeys = watch("campaign_context.campaign.language_targets");
	const appIdForFormat = watch("campaign_context.campaign.app_id");
	const appStoreForFormat = watch("campaign_context.campaign.app_store");

	const appIdFormatIssue = useMemo(
		() =>
			FormUtils.getAppIdFormatIssue(
				appIdForFormat ?? "",
				appStoreForFormat ?? "",
			),
		[appIdForFormat, appStoreForFormat],
	);

	// --------------- Effects
	// Сырой каталог стран/языков для селектов: кэш в localStorage по аккаунту, выбор — из полей формы
	useEffect(() => {
		if (!activeAdAccountId || !userId) return;

		let cancelled = false;
		const customerId = activeAdAccountId.replace(/-/g, "");
		const buyerId = Number(userId);

		const fillCountriesDropdown = (countries: GeoCountry[]) => {
			const items = countries.map((c) => ({
				key: String(c.criterion_id ?? c.code ?? c.country_code ?? c.name),
				node: <span>{c.name}</span>,
			}));
			setCountriesItems(items);
			const gKeys = getValues("campaign_context.campaign.geo_targets") ?? [];
			setSelectedCountries(
				gKeys.map((key) => {
					const found = items.find((i) => i.key === key);
					return found ?? { key, node: <span>{key}</span> };
				}),
			);
		};

		const fillLanguagesDropdown = (languages: Language[]) => {
			const items = languages.map((l) => ({
				key: l.code,
				node: <span>{l.name}</span>,
			}));
			setLanguagesItems(items);
			const lk = getValues("campaign_context.campaign.language_targets") ?? [];
			setSelectedLanguages(
				lk.map((key) => {
					const found = items.find((i) => i.key === key);
					return found ?? { key, node: <span>{key}</span> };
				}),
			);
		};

		const loadRawCountriesCatalog = async () => {
			setCountriesLoadingStatus(true);
			const cached = getRawGeoCountriesCatalog(buyerId, customerId);
			if (cached) {
				if (!cancelled) {
					fillCountriesDropdown(cached);
					setCountriesLoadingStatus(false);
				}
				return;
			}

			const result = await FormServices.fetchGeoCountries({
				buyer_id: buyerId,
				customer_id: customerId,
			});
			if (cancelled) return;
			setCountriesLoadingStatus(false);
			if (result.data?.ok && result.data.countries) {
				setRawGeoCountriesCatalog(buyerId, customerId, result.data.countries);
				fillCountriesDropdown(result.data.countries);
			}
		};

		const loadRawLanguagesCatalog = async () => {
			setLanguagesLoadingStatus(true);
			const cached = getRawLanguagesCatalog(buyerId, customerId);
			if (cached) {
				if (!cancelled) {
					fillLanguagesDropdown(cached);
					setLanguagesLoadingStatus(false);
				}
				return;
			}

			const result = await FormServices.fetchLanguages({
				buyer_id: buyerId,
				customer_id: customerId,
			});
			if (cancelled) return;
			setLanguagesLoadingStatus(false);
			if (result.data?.ok && result.data.languages) {
				setRawLanguagesCatalog(buyerId, customerId, result.data.languages);
				fillLanguagesDropdown(result.data.languages);
			}
		};

		void loadRawCountriesCatalog();
		void loadRawLanguagesCatalog();

		return () => {
			cancelled = true;
		};
	}, [activeAdAccountId, userId, getValues]);

	// Синхронизирует выбранные страны с полем geo_targets при изменении ключей или после догрузки каталога.
	useEffect(() => {
		setSelectedCountries(
			(geoKeys ?? []).map((key) => {
				const item = countriesItems.find((i) => i.key === key);
				return item ?? { key, node: <span>{key}</span> };
			}),
		);
	}, [geoKeys, countriesItems]);

	// Синхронизирует выбранные языки с language_targets при изменении ключей или каталога.
	useEffect(() => {
		setSelectedLanguages(
			(langKeys ?? []).map((key) => {
				const item = languagesItems.find((i) => i.key === key);
				return item ?? { key, node: <span>{key}</span> };
			}),
		);
	}, [langKeys, languagesItems]);

	const startDateWatch = watch("campaign_form_raw.start_date");
	const endDateWatch = watch("campaign_form_raw.end_date");

	// Проверяет согласованность start_date и end_date; при перепутанном порядке дат выставляет тексты ошибок под полями.
	useEffect(() => {
		if (!startDateWatch || !endDateWatch) {
			setStartDateError("");
			setEndDateError("");
			return;
		}
		if (new Date(endDateWatch) < new Date(startDateWatch)) {
			setStartDateError("Дата початку не може бути пізніше дати закінчення");
			setEndDateError("Дата закінчення не може бути раніше дати початку");
		} else {
			setStartDateError("");
			setEndDateError("");
		}
	}, [startDateWatch, endDateWatch]);

	// true, когда форму можно отправить: выбран аккаунт и выполнены правила isCampaignFormReadyForSubmit.
	const isFormReady = useMemo(
		() =>
			FormUtils.isCampaignFormReadyForSubmit(
				activeAdAccountId,
				formValues as ICampaignWizardForm,
				{ adGroupDraftsBlockSubmit },
			),
		[activeAdAccountId, formValues, adGroupDraftsBlockSubmit],
	);

	// Показывает/прячет анимированный оверлей «идёт запрос» в соответствии с session.freezeUI.
	useEffect(() => {
		FormUtils.syncFreezeTextLoaderAnimation(
			textLoaderRef.current,
			isFreezeUI,
			freezeUiWasActiveRef,
		);
	}, [isFreezeUI]);

	// --------------- Handlers
	// Конверсии
	const { onConversionSelect, onConversionRemove } =
		FormUtils.createConversionHandlers({
			allKey: ALL_KEY,
			conversionsSelectItems,
			getValues,
			setValue,
		});

	// Страны
	const { onCountrySelect, onCountryRemove } =
		FormUtils.createCountryTargetHandlers({
			allKey: ALL_KEY,
			countriesItems,
			getValues,
			setValue,
			setSelectedCountries,
		});

	// Языки
	const { onLanguageSelect, onLanguageRemove } =
		FormUtils.createLanguageTargetHandlers({
			allKey: ALL_KEY,
			languagesItems,
			getValues,
			setValue,
			setSelectedLanguages,
		});

	// После blur на app_id: при пустом значении сбрасывает каталог конверсий, иначе перезапрашивает его.
	const onAppIdBlurLocal = async (appIdRaw: string) => {
		const trimmed = String(appIdRaw).trim();
		if (!trimmed) {
			setConversionsCatalog([]);
			setConversionsCatalogLoading(false);
			return;
		}
		await refreshConversions();
	};

	// Сабмит мастера: нормализует поля, при необходимости создаёт сессию и requestAuth, отправляет payload через formService.
	const onSubmitCampaignWizard = handleSubmit(async (data) => {
		const aligned = FormUtils.alignCampaignFormRawFromContext(data);
		if (
			!FormUtils.isCampaignFormReadyForSubmit(activeAdAccountId, aligned, {
				adGroupDraftsBlockSubmit,
			})
		)
			return;

		const payloadWizard: ICampaignWizardForm = {
			...aligned,
			user_id: String(userId),
		};

		FormServices.formService.submitCampaignWizard(
			payloadWizard,
			String(userId),
			activeAdAccountId,
		);
	});

	return (
		<div className='form window'>
			<div className='window__header'>
				<div className='window__header_left'>
					<img src='/img/icons/form.png' alt='form icon' />
					<div>Google Ads</div>
				</div>
				<div className='window__header_center'>
					<FormComponents.CompactButton
						type='button'
						theme='green'
						className='form__create-button'
						unactive={!isFormReady || isFreezeUI}
						iconLeft={<img src='/img/icons/add.png' alt='add' />}
						onClick={() => void onSubmitCampaignWizard()}>
						Створити
					</FormComponents.CompactButton>
				</div>
				<div className='window__header_right'>
					<div
						className='window__header_right_close-button'
						role='button'
						aria-label='hide chat form'
						onClick={() => dispatch(FormUtils.hideModuleThunk("chat_form"))}>
						<img src='/img/icons/close.png' alt='close button' />
					</div>
				</div>
			</div>
			<div className='window__body'>
				<AdGroupDraftValidationProvider value={adGroupDraftValidationApi}>
					<form className={`form__wrapper${isFreezeUI ? " freeze-ui" : ""}`}>
						<FormComponents.TitlePath />
						<div className='form__fields'>
							<FormComponents.TextInputItem
								title='budget.amount (у валюті)*'
								type='number'
								min={0}
								{...budgetAmountRegister}
								error={budgetAmountErrorMessage}
							/>
							<FormComponents.TextInputItem
								title='campaign.name*'
								{...campaignNameRegister}
								error={campaignNameError}
							/>
							<div className='form__fields_field-item field-item'>
								<div className='field-item__title'>campaign.status*</div>
								<div className='field-item__input-wrapper'>
									<FormComponents.CompactSelectCampaignStatus
										control={control}
									/>
								</div>
							</div>
							<div className='form__fields_field-item field-item'>
								<div className='field-item__title'>app_campaign.app_store*</div>
								<div className='field-item__input-wrapper'>
									<FormComponents.CompactSelectAppStore control={control} />
								</div>
							</div>
							<div className='form__fields_field-item field-item'>
								<div className='field-item__title'>app_campaign.app_id*</div>
								<div className='field-item__input-wrapper'>
									<input
										type='text'
										className='field-item__input'
										name={appIdRegister.name}
										ref={appIdRegister.ref}
										onChange={appIdRegister.onChange}
										onBlur={(e) => {
											appIdRegister.onBlur(e);
											void onAppIdBlurLocal(e.target.value);
										}}
									/>
								</div>
								{appIdFormatIssue === "android" && (
									<div className='field-item__error'>
										App ID для Android повинен бути в форматі package name
										(наприклад, &apos;com.google.android.gm&apos;)
									</div>
								)}
								{appIdFormatIssue === "ios" && (
									<div className='field-item__error'>
										App ID для iOS повинен містити від 6 до 20 цифр (наприклад,
										&apos;422689480&apos;)
									</div>
								)}
							</div>
							<FormComponents.TextInputItem
								title='target_cpa (у валюті)*'
								type='number'
								min={0}
								{...targetCpaRegister}
								error={targetCpaErrorMessage}
							/>
							<FormComponents.CatalogMultiSelectField
								title='selective_optimization_conversion_actions*'
								availableItems={availableConversions}
								selectedItems={selectedConversions}
								onSelect={onConversionSelect}
								onRemove={onConversionRemove}
								isLoading={conversionsCatalogLoading}
								sentinelKey={ALL_KEY}
							/>
							<FormComponents.CatalogMultiSelectField
								title='geo_targets*'
								availableItems={availableCountries}
								selectedItems={selectedCountries}
								onSelect={onCountrySelect}
								onRemove={onCountryRemove}
								isLoading={countriesLoadingStatus}
								sentinelKey={ALL_KEY}
							/>
							<FormComponents.DateInputItem
								title='start_date'
								warning={startDateError}
								{...register("campaign_form_raw.start_date")}
							/>
							<FormComponents.DateInputItem
								title='end_date'
								warning={endDateError}
								{...register("campaign_form_raw.end_date")}
							/>
							<FormComponents.CatalogMultiSelectField
								title='language_targets'
								availableItems={availableLanguages}
								selectedItems={selectedLanguages}
								onSelect={onLanguageSelect}
								onRemove={onLanguageRemove}
								isLoading={languagesLoadingStatus}
								sentinelKey={ALL_KEY}
							/>
						</div>
						<FormComponents.GroupsInput
							key={`${sessionId ?? "none"}-${userId}-${fieldArrayMountKey}`}
						/>
					</form>
				</AdGroupDraftValidationProvider>
				<div
					ref={textLoaderRef}
					className='window__text-loader'
					style={{ opacity: 0, pointerEvents: "none" }}>
					<div className='window__text-loader_bg'></div>
					<FormComponents.DotsLoader />
					<div className='window__text-loader_text'>
						Будь ласка, зачекайте. Виконується запит
					</div>
				</div>
			</div>
		</div>
	);
};
